import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Input as AntInput, Modal, Select as AntSelect, Tabs, Cascader, Table, Tag } from "antd";
import { IArtist } from "../../../types/api/artist";
import { removeSpecialChars } from "../../../lib/utils";
import UploadImage from "../../components/UploadImage";
import { useArtistCreate, useArtistUpdate } from "../../../hooks/api/artist/hooks";
import { useCategoryList } from "../../../hooks/api/category/hooks";
import { useProductList } from "../../../hooks/api/product/hooks";
import ProductModal from "../product/ProductModal";
import { IProduct } from "../../../types/api/product";
import TinyMce from "../../../utils/TinyMCE";
import { ArtistFormValues } from "../../../types/api/artist";
import { useMessageApi } from "../../../provider/AntdNotification";

import { useCountryList, useStateList, useCityList } from "../../../hooks/api/location/hooks";
import ApiSelect from "../../components/ApiSelect";

const { TextArea } = AntInput;

const emptyValues: ArtistFormValues = {
  name: "",
  slug: "",
  email: "",
  password: "",
  location: {
    countryId: "",
    stateId: "",
    cityId: "",
  },
  profilePicture: null,
  telephone: "",
  mobile: "",
  alternativeContact: {
    name: "",
    relationship: "",
    email: "",
    phone: "",
  },
  biography: "",
  longBiography: "",
  artistStatement: "",
  qualifications: "",
  featuredArtwork: {
    image: null,
    title: "",
    description: "",
  },
  artGallery: [],
  awardsAndAchievements: "",
  exhibitions: "",
  publicWorks: "",
  publicAcquisitions: "",
  teachingExperience: "",
  influences: "",
  symposiums: "",
  reviews: "",
  publications: "",
  socialLinks: {
    instagram: "",
    facebook: "",
    twitter: "",
    pinterest: "",
    website: "",
    website2: "",
    linkedin: "",
    discord: "",
  },
  status: true,
};

interface ArtistModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItem?: IArtist | null;
  qData?: any;
}

const ArtistModal: React.FC<ArtistModalProps> = ({
  isOpen,
  onClose,
  selectedItem,
  qData,
}) => {
  const messageApi = useMessageApi();
  const { control, handleSubmit, reset, setValue, watch } =
    useForm<ArtistFormValues>({ defaultValues: emptyValues });

  const slugManuallyEdited = useRef(false);

  // Local, independent of the global modal store on purpose - that store
  // only tracks one open modal at a time, and this artist modal is already
  // occupying it. Opening a product edit through the global store here
  // would silently close the artist modal out from under the user instead
  // of layering on top of it (Oliver, Sept 2026: "Peter prefers to find an
  // artist first and edit their work from there").
  const [editingProduct, setEditingProduct] = useState<IProduct | null>(null);

  const createMutation = useArtistCreate({
    qData,
    onSuccess: () => {
      messageApi.success("Artist created successfully");
      onClose();
    },
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to create artist"),
  });
  const updateMutation = useArtistUpdate({
    qData,
    onSuccess: () => {
      messageApi.success("Artist updated successfully");
      onClose();
    },
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to update artist"),
  });

  useEffect(() => {
    if (!isOpen) return;
    slugManuallyEdited.current = !!selectedItem?.slug;

    if (selectedItem) {
      const locObj = (selectedItem as any)?.location || {};
      reset({
        name: selectedItem.name ?? "",
        slug: selectedItem.slug ?? "",
        email: selectedItem.email ?? "",
        password: "",
        location: {
          countryId: typeof locObj === "object" ? locObj.countryId?._id || locObj.countryId || "" : "",
          stateId: typeof locObj === "object" ? locObj.stateId?._id || locObj.stateId || "" : "",
          cityId: typeof locObj === "object" ? locObj.cityId?._id || locObj.cityId || "" : "",
        },
        profilePicture: selectedItem.profilePicture ?? null,
        telephone: (selectedItem as any).telephone ?? "",
        mobile: (selectedItem as any).mobile ?? "",
        alternativeContact: {
          name: (selectedItem as any).alternativeContact?.name ?? "",
          relationship: (selectedItem as any).alternativeContact?.relationship ?? "",
          email: (selectedItem as any).alternativeContact?.email ?? "",
          phone: (selectedItem as any).alternativeContact?.phone ?? "",
        },
        biography: selectedItem.biography ?? "",
        longBiography: selectedItem.longBiography ?? "",
        artistStatement: selectedItem.artistStatement ?? "",
        qualifications: selectedItem.qualifications ?? "",
        featuredArtwork: {
          image: selectedItem.featuredArtwork?.image ?? null,
          title: selectedItem.featuredArtwork?.title ?? "",
          description: selectedItem.featuredArtwork?.description ?? "",
        },
        artGallery: selectedItem.artGallery ?? [],
        awardsAndAchievements: selectedItem.awardsAndAchievements ?? "",
        exhibitions: selectedItem.exhibitions ?? "",
        publicWorks: selectedItem.publicWorks ?? "",
        publicAcquisitions: selectedItem.publicAcquisitions ?? "",
        teachingExperience: selectedItem.teachingExperience ?? "",
        influences: selectedItem.influences ?? "",
        symposiums: selectedItem.symposiums ?? "",
        reviews: selectedItem.reviews ?? "",
        publications: selectedItem.publications ?? "",
        socialLinks: {
          instagram: selectedItem.socialLinks?.instagram ?? "",
          facebook: selectedItem.socialLinks?.facebook ?? "",
          twitter: selectedItem.socialLinks?.twitter ?? "",
          pinterest: selectedItem.socialLinks?.pinterest ?? "",
          website: selectedItem.socialLinks?.website ?? "",
          website2: selectedItem.socialLinks?.website2 ?? "",
          linkedin: selectedItem.socialLinks?.linkedin ?? "",
          discord: selectedItem.socialLinks?.discord ?? "",
        },
        status: selectedItem.status ?? true,
      });
    } else {
      reset(emptyValues);
    }
  }, [isOpen, selectedItem, reset]);

  const nameValue = watch("name");
  const watchCountryId = watch("location.countryId");
  const watchStateId = watch("location.stateId");

  useEffect(() => {
    if (!slugManuallyEdited.current) {
      setValue("slug", removeSpecialChars(nameValue || ""));
    }
  }, [nameValue, setValue]);

  const onSubmit = (values: ArtistFormValues) => {
    // A plain string ("City, State, Country"), not the raw
    // {countryId,stateId,cityId} object the dropdowns work with - every
    // migrated artist's location is a free-text string, and the public
    // artist directory renders it straight into JSX. Saving the
    // structured object here (same bug the artist-portal's own profile
    // form had) gives that artist a location shape the directory can't
    // render at all - React throws on a raw object child, which crashes
    // the whole list the moment it scrolls to one of them (Oliver, Sept
    // 2026). The dropdowns above still work the same for editing either
    // way - the reset() effect already handles a plain-string profile.
    const cityName = cityOptions.find((o) => o.value === values.location?.cityId)?.label;
    const stateName = stateOptions.find((o) => o.value === values.location?.stateId)?.label;
    const countryName = countryOptions.find((o) => o.value === values.location?.countryId)?.label;
    const locationString = [cityName, stateName, countryName].filter(Boolean).join(", ");
    // Oliver, Oct 2026: 3 real artists (Liubka Kirilova among them) had a
    // perfectly good location silently wiped the moment someone saved an
    // unrelated field on their profile. Root cause: every migrated
    // artist's location is a plain string the pickers above can't read
    // (see the comment they already have), so an untouched-location edit
    // rebuilds `locationString` from a blank picker and overwrites the
    // real value with "". Never send an empty location over a real
    // existing one - only an intentional clear (picker touched, resolved
    // to nothing) should blank it, and that's indistinguishable here from
    // "never touched" for a string-location profile, so the safe
    // direction is to not overwrite rather than risk losing more data.
    const existingLocation = (selectedItem as any)?.location;
    const hadNonEmptyString =
      typeof existingLocation === "string" && existingLocation.trim().length > 0;
    const payload = {
      ...values,
      location: (locationString || (hadNonEmptyString ? existingLocation : "")) as any,
    };

    if (selectedItem) {
      updateMutation.mutate({ id: selectedItem._id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  const [countrySearch, setCountrySearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");

  const { data: countriesData } = useCountryList(
    { search: countrySearch, limit: 100 } as any,
    { enabled: isOpen },
  );

  const { data: statesData } = useStateList(
    { search: stateSearch, countryId: watchCountryId, limit: 100 } as any,
    { enabled: isOpen && !!watchCountryId },
  );

  const { data: citiesData } = useCityList(
    { search: citySearch, stateId: watchStateId, limit: 100 } as any,
    { enabled: isOpen && !!watchStateId },
  );

  // Oliver, Oct 2026: makes the fix above visible instead of just safe -
  // without this, a string-location artist's Country dropdown always
  // loads blank even though a correct value exists, so there was nothing
  // stopping the NEXT person who edits them from re-triggering the same
  // confusion (just no longer losing data over it). Once the country list
  // has loaded, try a one-time match of the free-text location's segments
  // against real country names - same approach as the public site's
  // LocationService.resolveCountryFromArtistLocation. Country only -
  // old free text is rarely precise enough to also trust for state/city,
  // and a user can still fill those in themselves from here.
  const resolvedLocationFor = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen || !selectedItem) return;
    const loc = (selectedItem as any)?.location;
    if (typeof loc !== "string" || !loc.trim()) return;
    if (resolvedLocationFor.current === selectedItem._id) return;
    if (!countriesData?.data?.length) return;

    const segments = loc
      .split(",")
      .map((s: string) => s.trim().toLowerCase())
      .filter(Boolean)
      .reverse();
    const match = (countriesData.data as any[]).find((c) =>
      segments.includes((c.name || "").toLowerCase()),
    );
    resolvedLocationFor.current = selectedItem._id;
    if (match) {
      setValue("location.countryId", match._id, { shouldDirty: false });
    }
  }, [isOpen, selectedItem, countriesData, setValue]);

  const countryOptions = React.useMemo(() => {
    const map = new Map<string, any>();
    const c: any = (selectedItem as any)?.location?.countryId;
    if (c && typeof c === "object" && c._id) {
      map.set(String(c._id), { label: c.name || "Country", value: c._id });
    }
    (countriesData?.data || []).forEach((item: any) => {
      if (item && item._id) map.set(String(item._id), { label: item.name, value: item._id });
    });
    return Array.from(map.values());
  }, [countriesData, selectedItem]);

  const stateOptions = React.useMemo(() => {
    const map = new Map<string, any>();
    const s: any = (selectedItem as any)?.location?.stateId;
    if (s && typeof s === "object" && s._id) {
      map.set(String(s._id), { label: s.name || "State", value: s._id });
    }
    (statesData?.data || []).forEach((item: any) => {
      if (item && item._id) map.set(String(item._id), { label: item.name, value: item._id });
    });
    return Array.from(map.values());
  }, [statesData, selectedItem]);

  const cityOptions = React.useMemo(() => {
    const map = new Map<string, any>();
    const ct: any = (selectedItem as any)?.location?.cityId;
    if (ct && typeof ct === "object" && ct._id) {
      map.set(String(ct._id), { label: ct.name || "City", value: ct._id });
    }
    (citiesData?.data || []).forEach((item: any) => {
      if (item && item._id) map.set(String(item._id), { label: item.name, value: item._id });
    });
    return Array.from(map.values());
  }, [citiesData, selectedItem]);

  const { data: artistProductsData, isLoading: isLoadingArtistProducts } =
    useProductList(
      { artistId: selectedItem?._id, limit: 50, page: 1 } as any,
      { enabled: isOpen && !!selectedItem?._id },
    );

  const handleCascaderChange = (value: any[][], onChange: (val: any) => void) => {
    onChange(value);
    // Flatten and get unique IDs for categoryIds
    const flatIds = Array.from(new Set(value.flat()));
    setValue("categoryIds", flatIds);
  };

  return (
    <>
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      title={selectedItem ? "Edit Artist" : "Add Artist"}
      width={800}
      className="top-5"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <Tabs
          defaultActiveKey="1"
          items={[
            {
              key: "1",
              label: "Basic Info",
              children: (
                <div className="flex flex-col gap-4 py-2">
                  <Controller
                    name="profilePicture"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex flex-col gap-1">
                        <label className="block text-sm font-medium">Profile Picture</label>
                        <UploadImage
                          value={value as string}
                          count={1}
                          listType="picture-card"
                          onChange={(url) => onChange(url as string)}
                        />
                      </div>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: "Please enter a name" }}
                      render={({ field, fieldState }) => (
                        <div>
                          <label className="block text-sm font-medium mb-1">Name *</label>
                          <AntInput
                            {...field}
                            placeholder="Artist Name"
                            status={fieldState.error ? "error" : ""}
                          />
                          {fieldState.error && (
                            <p className="text-xs text-red-500 mt-1">{fieldState.error.message}</p>
                          )}
                        </div>
                      )}
                    />
                    <Controller
                      name="slug"
                      control={control}
                      rules={{ required: "Slug is required" }}
                      render={({ field, fieldState }) => (
                        <div>
                          <label className="block text-sm font-medium mb-1">Slug *</label>
                          <AntInput
                            {...field}
                            placeholder="artist-slug"
                            status={fieldState.error ? "error" : ""}
                            onChange={(e) => {
                              slugManuallyEdited.current = true;
                              field.onChange(removeSpecialChars(e.target.value));
                            }}
                          />
                          {fieldState.error && (
                            <p className="text-xs text-red-500 mt-1">{fieldState.error.message}</p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Portal Credentials for existing/new artists */}
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex flex-col gap-3">
                    <p className="text-xs font-semibold text-slate-700">Artist Portal Credentials</p>
                    <div className="grid grid-cols-2 gap-4">
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <div>
                            <label className="block text-sm font-medium mb-1">Portal Email</label>
                            <AntInput {...field} type="email" placeholder="artist@example.com" />
                          </div>
                        )}
                      />
                      <Controller
                        name="password"
                        control={control}
                        render={({ field }) => (
                          <div>
                            <label className="block text-sm font-medium mb-1">
                              {selectedItem ? "Set / Reset Password" : "Password"}
                            </label>
                            <AntInput.Password
                              {...field}
                              placeholder={selectedItem ? "Leave empty to keep current" : "Minimum 6 characters"}
                            />
                          </div>
                        )}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <Controller
                      name="location.countryId"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <label className="block text-sm font-medium mb-1">Country</label>
                          <ApiSelect
                            {...field}
                            className="w-full"
                            placeholder="Select Country"
                            options={countryOptions}
                            onChange={(val) => {
                              field.onChange(val);
                              setValue("location.stateId", undefined as any);
                              setValue("location.cityId", undefined as any);
                            }}
                            refetchWithQuery={(q: any) => setCountrySearch(q?.search || "")}
                            baseQuery={{ limit: 100 }}
                          />
                        </div>
                      )}
                    />
                    <Controller
                      name="location.stateId"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <label className="block text-sm font-medium mb-1">State / County / Province</label>
                          <ApiSelect
                            {...field}
                            className="w-full"
                            placeholder="Select State / County / Province"
                            disabled={!watchCountryId}
                            options={stateOptions}
                            onChange={(val) => {
                              field.onChange(val);
                              setValue("location.cityId", undefined as any);
                            }}
                            refetchWithQuery={(q: any) => setStateSearch(q?.search || "")}
                            baseQuery={{ countryId: watchCountryId, limit: 100 }}
                          />
                        </div>
                      )}
                    />
                    <Controller
                      name="location.cityId"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <label className="block text-sm font-medium mb-1">City</label>
                          <ApiSelect
                            {...field}
                            className="w-full"
                            placeholder="Select City"
                            disabled={!watchStateId}
                            options={cityOptions}
                            onChange={field.onChange}
                            refetchWithQuery={(q: any) => setCitySearch(q?.search || "")}
                            baseQuery={{ stateId: watchStateId, limit: 100 }}
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <label className="block text-sm font-medium mb-1">Status</label>
                          <AntSelect
                            value={field.value}
                            onChange={field.onChange}
                            options={[
                              { label: "Active", value: true },
                              { label: "Inactive", value: false },
                            ]}
                            className="w-full"
                          />
                        </div>
                      )}
                    />
                  </div>
                </div>
              ),
            },
            {
              key: "2",
              label: "Biography & Links",
              children: (
                <div className="flex flex-col gap-4 py-2">
                  <Controller
                    name="biography"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label className="block text-sm font-medium mb-1">Biography</label>
                        <TinyMce value={field.value} onChange={field.onChange} />
                      </div>
                    )}
                  />
                  <Controller
                    name="longBiography"
                    control={control}
                    render={({ field }) => (
                      <div className="mt-4">
                        <label className="block text-sm font-medium mb-1">Extended / CV Biography</label>
                        <TinyMce value={field.value} onChange={field.onChange} />
                      </div>
                    )}
                  />
                  <Controller
                    name="artistStatement"
                    control={control}
                    render={({ field }) => (
                      <div className="mt-4">
                        <label className="block text-sm font-medium mb-1">Artist Statement</label>
                        <TinyMce value={field.value} onChange={field.onChange} />
                      </div>
                    )}
                  />
                  <Controller
                    name="qualifications"
                    control={control}
                    render={({ field }) => (
                      <div className="mt-4">
                        <label className="block text-sm font-medium mb-1">Qualifications</label>
                        <TinyMce value={field.value} onChange={field.onChange} />
                      </div>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    {/* Free-text, not validated as a strict phone number -
                        real entries include extensions and notes like
                        "Vic 020 83928585" (Oliver's phone import, Sept 2026). */}
                    <Controller name="telephone" control={control} render={({ field }) => (
                      <div><label className="block text-sm font-medium mb-1">Telephone</label><AntInput {...field} /></div>
                    )} />
                    <Controller name="mobile" control={control} render={({ field }) => (
                      <div><label className="block text-sm font-medium mb-1">Mobile</label><AntInput {...field} /></div>
                    )} />
                  </div>

                  {/* Peter, Oct 2026: needed for when an artist is away,
                      or has died and we have no way to reach next of kin -
                      especially a woman who worked under her unmarried
                      name, where the widower's details were never on
                      file anywhere. */}
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs font-semibold text-slate-700 mb-3">
                      Alternative Contact (wife / partner / secretary, etc.)
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <Controller name="alternativeContact.name" control={control} render={({ field }) => (
                        <div><label className="block text-sm font-medium mb-1">Name</label><AntInput {...field} placeholder="e.g. Jane Smith" /></div>
                      )} />
                      <Controller name="alternativeContact.relationship" control={control} render={({ field }) => (
                        <div><label className="block text-sm font-medium mb-1">Relationship</label><AntInput {...field} placeholder="e.g. Wife, Partner, Secretary" /></div>
                      )} />
                      <Controller name="alternativeContact.email" control={control} render={({ field }) => (
                        <div><label className="block text-sm font-medium mb-1">Email</label><AntInput {...field} type="email" /></div>
                      )} />
                      <Controller name="alternativeContact.phone" control={control} render={({ field }) => (
                        <div><label className="block text-sm font-medium mb-1">Phone</label><AntInput {...field} /></div>
                      )} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <Controller name="socialLinks.instagram" control={control} render={({ field }) => (
                      <div><label className="block text-sm font-medium mb-1">Instagram URL</label><AntInput {...field} /></div>
                    )} />
                    <Controller name="socialLinks.facebook" control={control} render={({ field }) => (
                      <div><label className="block text-sm font-medium mb-1">Facebook URL</label><AntInput {...field} /></div>
                    )} />
                    <Controller name="socialLinks.twitter" control={control} render={({ field }) => (
                      <div><label className="block text-sm font-medium mb-1">Twitter URL</label><AntInput {...field} /></div>
                    )} />
                    <Controller name="socialLinks.linkedin" control={control} render={({ field }) => (
                      <div><label className="block text-sm font-medium mb-1">LinkedIn URL</label><AntInput {...field} /></div>
                    )} />
                    <Controller name="socialLinks.pinterest" control={control} render={({ field }) => (
                      <div><label className="block text-sm font-medium mb-1">Pinterest URL</label><AntInput {...field} /></div>
                    )} />
                    <Controller name="socialLinks.website" control={control} render={({ field }) => (
                      <div><label className="block text-sm font-medium mb-1">Website URL</label><AntInput {...field} /></div>
                    )} />
                    <Controller name="socialLinks.website2" control={control} render={({ field }) => (
                      <div><label className="block text-sm font-medium mb-1">Second Website URL</label><AntInput {...field} /></div>
                    )} />
                  </div>
                </div>
              ),
            },
            {
              key: "3",
              label: "Featured Artwork",
              children: (
                <div className="flex flex-col gap-4 py-2">
                  <Controller
                    name="featuredArtwork.image"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex flex-col gap-1">
                        <label className="block text-sm font-medium">Artwork Image</label>
                        <UploadImage
                          value={value as string}
                          count={1}
                          listType="picture-card"
                          onChange={(url) => onChange(url as string)}
                        />
                      </div>
                    )}
                  />
                  <Controller
                    name="featuredArtwork.title"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label className="block text-sm font-medium mb-1">Artwork Title</label>
                        <AntInput {...field} placeholder="e.g. Wild Wisdom" />
                      </div>
                    )}
                  />
                  <Controller
                    name="featuredArtwork.description"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label className="block text-sm font-medium mb-1">Artwork Description</label>
                        <TextArea {...field} rows={4} placeholder="Description about the artwork..." />
                      </div>
                    )}
                  />

                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <Controller
                      name="artGallery"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <div className="flex flex-col gap-1">
                          <label className="block text-sm font-medium mb-1">Art Gallery Images</label>
                          <UploadImage
                            value={value as unknown as string} // Since it handles arrays if properly configured, but let's assume it accepts array or multiple uploads if count > 1
                            count={10}
                            listType="picture-card"
                            onChange={(urls) => onChange(urls)}
                          />
                        </div>
                      )}
                    />
                  </div>
                </div>
              ),
            },
            {
              key: "4",
              label: "Achievements & Works",
              children: (
                <div className="flex flex-col gap-4 py-2">
                  <Controller
                    name="awardsAndAchievements"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label className="block text-sm font-medium mb-1">Awards & Achievements</label>
                        <TinyMce value={field.value} onChange={field.onChange} />
                      </div>
                    )}
                  />
                  <Controller
                    name="exhibitions"
                    control={control}
                    render={({ field }) => (
                      <div className="mt-4">
                        <label className="block text-sm font-medium mb-1">Exhibitions</label>
                        <TinyMce value={field.value} onChange={field.onChange} />
                      </div>
                    )}
                  />
                  <Controller
                    name="publicWorks"
                    control={control}
                    render={({ field }) => (
                      <div className="mt-4">
                        <label className="block text-sm font-medium mb-1">Public Works</label>
                        <TinyMce value={field.value} onChange={field.onChange} />
                      </div>
                    )}
                  />
                  <Controller
                    name="publicAcquisitions"
                    control={control}
                    render={({ field }) => (
                      <div className="mt-4">
                        <label className="block text-sm font-medium mb-1">Public Acquisitions</label>
                        <TinyMce value={field.value} onChange={field.onChange} />
                      </div>
                    )}
                  />
                  <Controller
                    name="teachingExperience"
                    control={control}
                    render={({ field }) => (
                      <div className="mt-4">
                        <label className="block text-sm font-medium mb-1">Teaching Experience</label>
                        <TinyMce value={field.value} onChange={field.onChange} />
                      </div>
                    )}
                  />
                  <Controller
                    name="influences"
                    control={control}
                    render={({ field }) => (
                      <div className="mt-4">
                        <label className="block text-sm font-medium mb-1">Influences</label>
                        <TinyMce value={field.value} onChange={field.onChange} />
                      </div>
                    )}
                  />
                  <Controller
                    name="symposiums"
                    control={control}
                    render={({ field }) => (
                      <div className="mt-4">
                        <label className="block text-sm font-medium mb-1">Symposiums</label>
                        <TinyMce value={field.value} onChange={field.onChange} />
                      </div>
                    )}
                  />
                  <Controller
                    name="reviews"
                    control={control}
                    render={({ field }) => (
                      <div className="mt-4">
                        <label className="block text-sm font-medium mb-1">Reviews</label>
                        <TinyMce value={field.value} onChange={field.onChange} />
                      </div>
                    )}
                  />
                  <Controller
                    name="publications"
                    control={control}
                    render={({ field }) => (
                      <div className="mt-4">
                        <label className="block text-sm font-medium mb-1">Publications</label>
                        <TinyMce value={field.value} onChange={field.onChange} />
                      </div>
                    )}
                  />
                </div>
              ),
            },
            {
              key: "5",
              label: "Sculptures",
              children: selectedItem ? (
                <Table
                  size="small"
                  loading={isLoadingArtistProducts}
                  dataSource={artistProductsData?.data || []}
                  rowKey="_id"
                  pagination={false}
                  onRow={(record: any) => ({
                    onClick: () => setEditingProduct(record),
                    style: { cursor: "pointer" },
                  })}
                  columns={[
                    {
                      title: "Image",
                      dataIndex: "thumbnail",
                      key: "thumbnail",
                      render: (thumbnail?: string) =>
                        thumbnail ? (
                          <img
                            src={thumbnail}
                            alt="Sculpture"
                            className="w-10 h-10 object-cover rounded border border-slate-200"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center text-slate-400 text-[9px]">
                            No Image
                          </div>
                        ),
                    },
                    { title: "Title", dataIndex: "title", key: "title" },
                    {
                      title: "Price",
                      key: "price",
                      render: (_: any, record: any) =>
                        record.isPriceOnApplication ? (
                          <Tag color="orange">POA</Tag>
                        ) : typeof record.price === "number" && record.price > 0 ? (
                          `£${record.price.toLocaleString("en-GB")}`
                        ) : (
                          <span className="text-slate-400">-</span>
                        ),
                    },
                    {
                      title: "Status",
                      dataIndex: "status",
                      key: "status",
                      render: (status: boolean) => (
                        <Tag color={status ? "green" : "red"}>
                          {status ? "Active" : "Inactive"}
                        </Tag>
                      ),
                    },
                  ]}
                />
              ) : (
                <div className="p-4 text-center text-slate-500 bg-slate-50 rounded-md border border-dashed border-slate-300">
                  Please save the artist first to see their sculptures.
                </div>
              ),
            },
          ]}
        />

        <div className="flex justify-end gap-2 mt-2 pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="px-4 py-1.5 rounded-md bg-primary text-white disabled:opacity-60 transition-colors"
          >
            {isPending ? "Saving..." : selectedItem ? "Update" : "Create"}
          </button>
        </div>
      </form>
    </Modal>

    {/* Opening a sculpture from the artist's own Sculptures tab, editions
        included, instead of having to close this modal and go find it in
        the main Products list (Oliver, Sept 2026). */}
    <ProductModal
      isOpen={!!editingProduct}
      onClose={() => setEditingProduct(null)}
      selectedItem={editingProduct}
      qData={selectedItem?._id ? { artistId: selectedItem._id, limit: 50, page: 1 } : undefined}
    />
    </>
  );
};

export default ArtistModal;
